import { z } from 'zod'

/**
 * A date that survives every representation it passes through:
 * ISO string in `event.config.ts`, Firestore `Timestamp` on the server,
 * `{_seconds}` after JSON transport from a callable, `Date` in tests.
 */
export const zDate = z.preprocess((value) => {
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? value : parsed
  }
  if (value && typeof value === 'object') {
    const o = value as { toDate?: unknown; seconds?: unknown; _seconds?: unknown }
    if (typeof o.toDate === 'function') return (o.toDate as () => Date)()
    const seconds = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null
    if (seconds !== null) return new Date(seconds * 1000)
  }
  return value
}, z.date())

/** Thai mobile/landline, stored digits-only. */
export const zPhone = z
  .string()
  .trim()
  .transform((s) => s.replace(/[\s()-]/g, ''))
  .pipe(z.string().regex(/^0\d{8,9}$/, 'เบอร์โทรไม่ถูกต้อง (ต้องเป็นเลข 9-10 หลัก ขึ้นต้นด้วย 0)'))

export const zEmail = z.string().trim().toLowerCase().email('รูปแบบอีเมลไม่ถูกต้อง')

export const zPostcode = z.string().trim().regex(/^\d{5}$/, 'รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก')

/** Money in whole baht. Fractional baht does not exist in this product. */
/**
 * Money, to the satang.
 *
 * It used to be whole baht, which was true right up until VAT was added on top of
 * the item price: 7% of ฿263 is ฿18.41, and an integer type can only answer that by
 * being wrong by up to half a baht on every order. Beam settles in satang anyway, so
 * two decimal places is what the payment actually supports.
 *
 * A third decimal is still refused — there is no such coin, and a figure nobody can
 * transfer has no business on an invoice.
 */
export const zBaht = z
  .number()
  .min(0)
  .refine((value) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6, {
    message: 'จำนวนเงินมีทศนิยมได้ไม่เกิน 2 ตำแหน่ง',
  })

/** Reject codes that would collide once compared case-insensitively. */
export const uniqueBy = <T>(rows: readonly T[], key: (row: T) => string): boolean => {
  const seen = new Set<string>()
  for (const row of rows) {
    const k = key(row).toLowerCase()
    if (seen.has(k)) return false
    seen.add(k)
  }
  return true
}
